<template>
<div :id="componentId">
  <div class="toolbar mb8">
    <select class="custom-select" v-model="selectedRoadName"
        @change="onChangeRoadName">
      <option v-for="e in roadNames" :value="e.key" :key="e.key">
        {{e.val}}
      </option>
    </select>
    <sort-button class="ib sort-bt1 ml4" @sort="onRegIdSort"
        :toggle-type="sort.regId.toggleType">
      登録番号
    </sort-button>
  </div>
  <div :id="listWrapId" class="list-wrap">
    <b-list-group class="selectable">
      <b-list-group-item v-for="e in displayedJointMasters" :key="e.register_id"
        :id="listItemId(e)"
        :class="getListItemClz(e)"
        @click="onListItemClick(e)"
        v-html="itemDisp(e)">
      </b-list-group-item>
    </b-list-group>
  </div>
  <div class="mt10">
    <div class="ta-right count-info" v-if="countInfo.totalCount">
      <span class="mr4">完了数</span>
      <span class="done-count">{{countInfo.doneCount}}</span>
      <span class="slash ml2 mr2">/</span>
      <span class="total-count">{{countInfo.totalCount}}</span>
    </div>
  </div>
  <pager class="mt8" v-show="false"
    :item-count="itemCount"
    :items-per-page="itemsPerPage"
    :page-range="1"
    :margin-pages="0"
    :force-page="currentPage - 1"
    @click="onChangePage"
  ></pager>
</div>
</template>

<script>
import Vue from 'vue'
import sortMixin from '@/mixin/sortMixin'
import * as eventNames from '@/consts/eventNames'
import * as sortStates from '@/consts/sortStates'

export default {
  name: 'joint-master-list',
  props: {
    src: {
      type: Array,
      default() {
        return []
      }
    },
    dispType: {
      type: String,
      default: 'full',
    },
    stream: {
      type: Object,
    }
  },
  data() {
    return {
      isReady: false,

      filteredJointMasters: [],
      displayedJointMasters: [],
      jointMastersByRoadName: {},
      jointMasterMap: {}, // by joint_group_key
      selectedJointMastersMap: {},

      selectedJointsMap: {},

      sort: {
        regId: {
          toggleType: sortStates.TOGGLE_TYPE_AD,
          state: sortStates.SORT_STATE_ASC,
        }
      },

      roadNames: [],
      selectedRoadName: '',

      onResizeFunc: null,

      itemsPerPage: 10000, // no paging
      currentPage: 1,

      countInfo: {
        doneCount: 0,
        totalCount: 0,
      }
    }
  },
  computed: {
    componentId() {
      return 'joint-master-list-' + this._uid
    },
    listWrapId() {
      return 'list-wrap-' + this._uid
    },
    itemCount() {
      return this.filteredJointMasters.length
    },
    isSelectedJointsEmpty() {
      return Object.keys(this.selectedJointsMap).length === 0
    },
    isSelectedJointMastersEmpty() {
      return Object.keys(this.selectedJointMastersMap).length === 0
    },
  },
  watch: {
    src() {
      const { jointMastersByRoadName, jointMasterMap, roadNames } =
        this.convJointMasters(this.src.slice())
      this.jointMastersByRoadName = jointMastersByRoadName
      this.jointMasterMap = jointMasterMap
      this.roadNames = roadNames

      let prms = Promise.resolve()
      if (!this.selectedRoadName) {
        prms = this.$store.dispatch(
          'localStorage/get',
          { key: 'selectedRoadNameJointMaster' }
        ).then(name => {
          this.selectedRoadName = name || this.roadNames[0].key
        })
      }
      prms.then(() => {
        this.isReady = true
        this.onChangeRoadName()
      })
    },
  },
  mixins: [sortMixin],
  mounted() {
    this.initResizeFunc()
    this.setListeners()
  },
  destroyed() {
    window.removeEventListener('resize', this.onResizeFunc)
  },
  methods: {
    listItemId(e) {
      return `list-item-${e.register_id}-${this._uid}`
    },
    getListItemClz(e) {
      // order matters
      const arr = []
      if (e.is_attached) {
        arr.push('attached')
      }
      if (this.selectedJointMastersMap[e.joint_group_key]) {
        arr.push('selected')
      }
      return arr.join(' ')
    },
    initResizeFunc() {
      this.onResizeFunc = () => {
        const h = window.innerHeight - 170
        const elem = document.getElementById(this.listWrapId)
        elem.style.height = `${h}px`
      }
      this.onResizeFunc()
      window.addEventListener('resize', this.onResizeFunc)
    },
    setListeners() {
      this.stream.recv(eventNames.SELECT_JOINTS,
        ({ joints }) => {
          if (!this.isReady) { return }
          if (!joints) { return }
          const obj = joints.reduce((acc, e) => {
            acc[e.id] = e; return acc
          }, {})
          this.selectedJointsMap = obj
          this.setJointMasterSelections(joints)
          this.scrollToItemByJoint(joints[0])
        }
      )
      this.stream.recv(eventNames.UNSELECT_JOINTS,
        ({ joints }) => {
          if (!this.isReady) { return }
          if (!joints) { return }
          joints.forEach(e => {
            Vue.delete(this.selectedJointsMap, e.id)
          })
          this.clearJointMasterSelections()
        }
      )
      this.stream.recv(eventNames.UPDATED_JOINT_MASTERS,
        ({ jointMasters }) => {
          if (!this.isReady) { return }
          if (!jointMasters) { return }
          // 同じジョイント名を持った複数のジョイントが
          // きてるので、ジョイント名ごとに一回処理が
          // 行われるようにする. (register_idが合ってなくても
          // 問題ない)
          const tmpObj = jointMasters.reduce((acc, e) => {
            acc[e.joint_group_key] = e; return acc
          }, {})
          jointMasters = Object.values(tmpObj)
          jointMasters.forEach(jm => {
            // プロパティだけ差し替える.
            // なんとこれでリストの表示が更新されるので便利だ.
            const target = this.jointMasterMap[jm.joint_group_key]
            target.is_attached = jm.is_attached
            target.attached_joint_id = jm.attached_joint_id
            // 選択を解除
            Vue.delete(this.selectedJointMastersMap, jm.joint_group_key)
          })
          // 完了数を更新
          this.updateCounts()
        }
      )
    },
    scrollToItemByJoint(joint) {
      if (!joint.joint_number) { return }
      const jm = this.jointMasterMap[joint.joint_number]
      if (!jm) {
        console.warn('scrollToItemByJoint. jointMaster not found for ', joint)
        return
      }

      // select roadname
      if (this.selectedRoadName !== jm.road_name) {
        this.selectedRoadName = jm.road_name
        this.onChangeRoadName()
      }

      // scroll
      setTimeout(() => {
        window.scrollManager.scrollTo(
          '#' + this.listItemId(jm),
          200,
          {
            offset: -10,
            container: '#' + this.listWrapId,
          }
        )
      }, 0)
    },
    itemDisp(e) {
      if (this.dispType === 'short1') {
        return this.shortItemDisp1(e)
      } else {
        return 'Wrong disp type'
      }
    },
    shortItemDisp1(e) {
      const regIds = e.joint_group_members.map(e => e.register_id)
      const materialTypes = e.joint_group_members.map(e => e.material_type)
      const kpDisp = e.kp_prefix + (e.kp === null ? '??' : e.kp)
      return `
        <div class="item-disp">
          <p class="item-disp-row row1">
            【${e.joint_number}】
            <span class="road-name">${e.road_name}</span>
            <span class="road-section-type">${e.road_section_type}</span>
          </p>
          <p class="item-disp-row row2">
            方向: <span class="direction mr4">${e.direction}</span>
            kp: <span class="kp">${kpDisp}</span>
          </p>
          <p class="item-disp-row row3">
            車線: ${e.road_lanes.join(', ')}
          </p>
          <p class="item-disp-row row4">
            登録番号: ${regIds.join(', ')}
          </p>
          <p class="item-disp-row row5">
            材質: ${materialTypes.join(', ')}
          </p>
        </div>
      `
    },
    convJointMasters(arr) {
      // 路線ごとにまとめる
      // さらにジョイント番号でまとめる
      const jointMastersAll = {}
      const roadNameMap = {}
      for (const e of arr) {
        const roadName = e.road_name
        roadNameMap[roadName] = 1
        if (!jointMastersAll[roadName]) {
          jointMastersAll[roadName] = {}
        }
        const jm = jointMastersAll[roadName]
        // 複数のジョイントをひとまとめで管理するのでいい感じのキー
        // (路線名も込みでまとまりごとにむりやりユニーク)
        const k = e.joint_group_key
        // キーが同じやつを集積
        if (!jm[k]) {
          jm[k] = {}
        }
        jm[k][e.register_id] = e
      }

      const jointMasterMap = {}
      // 路線ごとに
      Object.keys(roadNameMap).forEach(roadName => {
        // objからarrに差し替え
        const jm = jointMastersAll[roadName]
        const arr = []
        Object.keys(jm).forEach(k => {
          const obj = jm[k]
          // const [_ignore1, _ignore2, _ignore3, jointNumber, _ignore4, _ignore5] = k.split('###')
          const jointNumber = k.split('###')[3]
          const roadLanes = []
          const jointGroupMembers = []
          Object.keys(obj).sort().forEach(regId => {
            roadLanes.push(obj[regId].road_lane_type)
            jointGroupMembers.push(obj[regId])
          })

          // 代表要素を選出
          const joint = Object.assign({}, jointGroupMembers[0])
          joint.road_lanes = roadLanes
          joint.joint_group_members = jointGroupMembers
          arr.push(joint)
          jointMasterMap[k] = joint

          // kpあってるかなんとなくチェック
          let kp = null
          Object.keys(obj).forEach(regId => {
            if (kp !== null && kp !== obj[regId].kp) {
              console.error('kp does not match')
              console.error(`joint_number=${jointNumber}`)
            }
            kp = obj[regId].kp
          })
        })
        jointMastersAll[roadName] = arr
      })

      const roadNames = Object.keys(roadNameMap).sort((a, b) => {
        let v1 = a
        let v2 = b
        const m1 = a.match(/高速(\d+)号/)
        const m2 = b.match(/高速(\d+)号/)
        if (m1 && m2) {
          v1 = parseInt(m1[1])
          v2 = parseInt(m2[1])
        }
        return v1 < v2 ? -1 : (v1 > v2 ? 1 : 0)
      }).map(e => {
        return {
          key: e,
          val: '路線名: ' + e
        }
      })

      return { jointMastersByRoadName: jointMastersAll, jointMasterMap, roadNames }
    },
    onChangeRoadName() {
      this.currentPage = 1
      this.updateFilteredJointMasters()
      const elem = document.getElementById(this.listWrapId)
      elem.scroll(0, 0)

      const roadName = this.selectedRoadName
      this.stream.send(eventNames.CHANGE_ROAD_NAME, { roadName })
      this.$store.dispatch(
        'localStorage/set',
        {
          key: 'selectedRoadNameJointMaster',
          val: roadName,
        }
      )
    },
    onChangePage(page) {
      this.currentPage = page
      this.setDisplayedJointMasters()
      const elem = document.getElementById(this.listWrapId)
      elem.scroll(0, 0)
    },
    updateFilteredJointMasters() {
      const arr = this.jointMastersByRoadName[this.selectedRoadName]
      this.filteredJointMasters = this.sortedArrayBy(arr,
        [
          { prop: 'register_id', sortState: this.sort.regId.state },
        ])
      this.updateCounts()
      this.setDisplayedJointMasters()
    },
    setDisplayedJointMasters() {
      const startIdx = this.itemsPerPage * Math.max(this.currentPage - 1, 0)
      this.displayedJointMasters = this.filteredJointMasters.slice(
        startIdx, startIdx + this.itemsPerPage)
      this.stream.send(eventNames.CHANGE_JOINT_MASTERS,
        { jointMasters: this.displayedJointMasters })
    },
    updateCounts() {
      this.countInfo.totalCount = this.filteredJointMasters.length
      this.countInfo.doneCount =
        this.filteredJointMasters.filter(jm => {
          return jm.is_attached
        }).length
    },
    onListItemClick(jm) {
      if (this.selectedJointMastersMap[jm.joint_group_key]) {
        // 今選択されてたものがクリックされたので紐付け解除
        this.stream.send(eventNames.UNSELECT_JOINT_MASTERS,
          {
            joints: Object.values(this.selectedJointsMap),
            jointMasters: jm.joint_group_members
          }
        )
        return
      } else if (jm.is_attached) {
        // 紐付け済のものがクリックされたらそれのみを通知
        this.stream.send(eventNames.SELECT_JOINT_MASTERS,
          {
            joints: [],
            jointMasters: jm.joint_group_members
          }
        )
        return
      }

      // ジョイントが選択されてなければここで終了
      if (this.isSelectedJointsEmpty) { return }

      // 選択がある状態で別のやつをクリックしたので
      // 元々選択されてたやつを紐付け解除用の配列に保存
      let oldJointMasters = []
      if (!this.isSelectedJointMastersEmpty) {
        oldJointMasters = Object.values(this.selectedJointMastersMap)
      }

      // select
      this.stream.send(eventNames.SELECT_JOINT_MASTERS,
        {
          joints: Object.values(this.selectedJointsMap),
          jointMasters: jm.joint_group_members,
          oldJointMasters,
        }
      )
    },
    clearJointMasterSelections() {
      this.selectedJointMastersMap = {}
    },
    setJointMasterSelections(joints) {
      this.selectedJointMastersMap = joints
        .filter(e => !!e.joint_number)
        .reduce((acc, e) => {
          const jointGroupKey = e.joint_number
          const jmObj = this.jointMasterMap[jointGroupKey]
          if (!jmObj) { return acc }
          acc[jointGroupKey] = jmObj
          return acc
        }, {})
    },
    onRegIdSort({ sortState }) {
      this.sort.regId.state = sortState
      this.updateFilteredJointMasters()
    },
  }
}
</script>

<style lang="scss" scoped>
.custom-select {
  width: 67%;
}
.sort-bt1 {
  width: 30%;
}
.count-info {
  font-size: 0.9rem;
}
</style>
